import React, { useMemo } from 'react'
import { ComposableMap, Geographies, Geography } from 'react-simple-maps'

import { Stack, EmptyState } from '@pnld/components-web'

import { useQueryBrazilStatesGeo } from '@/api/assets/queries'

import styles from './styles'
import type {
  ComposableMapCustomProps,
  GeographiesCustomProps,
  GeographyCustomProps,
  MapProps,
  GeoJsonFeature,
  States,
  Regions,
} from './types'

const ComposableMapCustom =
  ComposableMap as unknown as React.ComponentType<ComposableMapCustomProps>

const GeographiesCustom =
  Geographies as unknown as React.ComponentType<GeographiesCustomProps>

const GeographyCustom =
  Geography as unknown as React.ComponentType<GeographyCustomProps>

const Map: React.FC<MapProps> = ({
  region,
  selectedStates = [],
  selectedRegions = [],
  isClickable = false,
  isHoverable = false,
  onStateClick,
  onStateMouseEnter,
  onStateMouseLeave,
}) => {
  const { mapColors } = styles

  const { data: geoData, isLoading, isError, error } = useQueryBrazilStatesGeo()

  const handleGeographyClick = (geo: GeoJsonFeature) => {
    if (isClickable && onStateClick) onStateClick(geo.id)
  }

  const getGeographyStyle = (geo: GeoJsonFeature) => {
    const isSelected =
      selectedStates.includes(geo.id as States) ||
      selectedRegions.includes(geo.properties?.FK_macro as Regions)

    const allowHover = isClickable || isHoverable

    return {
      default: {
        fill: isSelected ? mapColors.selected : mapColors.primary,
        outline: 'none',
        stroke: mapColors.stroke,
        strokeWidth: 0.5,
      },
      hover: allowHover
        ? {
            fill: isSelected ? mapColors.hoverSelected : mapColors.hover,
            outline: 'none',
            stroke: isSelected ? mapColors.hoverSelected : mapColors.stroke,
            strokeWidth: 0.5,
            cursor: isClickable
              ? 'pointer'
              : isHoverable
              ? 'pointer'
              : 'default',
          }
        : {
            fill: isSelected ? mapColors.selected : mapColors.primary,
            outline: 'none',
            stroke: mapColors.stroke,
            strokeWidth: 0.5,
            cursor: 'default',
          },
      pressed: isClickable
        ? {
            fill: isSelected ? mapColors.selected : mapColors.primary,
            outline: 'none',
            stroke: mapColors.stroke,
            strokeWidth: 0.5,
          }
        : {
            fill: isSelected ? mapColors.selected : mapColors.primary,
            outline: 'none',
            stroke: mapColors.stroke,
            strokeWidth: 0.5,
          },
    }
  }

  const filterGeographies = useMemo(
    () => (geographies: GeoJsonFeature[]) => {
      if (!region) {
        return geographies
      }

      return geographies.filter(geo => geo.properties?.FK_macro === region)
    },
    [region]
  )

  const projectionCofig = useMemo(() => {
    if (region === 'Norte') {
      return { center: [-60, -4.5] as [number, number], scale: 1000 }
    }

    if (region === 'Nordeste') {
      return { center: [-41.6, -9] as [number, number], scale: 1600 }
    }

    if (region === 'Centro-Oeste') {
      return { center: [-54, -15.8] as [number, number], scale: 1600 }
    }

    if (region === 'Sudeste') {
      return { center: [-46.5, -20] as [number, number], scale: 2000 }
    }

    if (region === 'Sul') {
      return { center: [-52.7, -28.2] as [number, number], scale: 2300 }
    }

    return { center: [-54, -14] as [number, number], scale: 700 }
  }, [region])

  return (
    <styles.MapContainer>
      <styles.MapWrapper>
        {(isLoading || (isError && error)) && (
          <Stack
            height="100%"
            width="100%"
            alignItems="center"
            justifyContent="center"
          >
            <EmptyState
              title={
                isLoading
                  ? 'O mapa está sendo carregado'
                  : 'Erro ao carregar o mapa'
              }
              description={
                isLoading
                  ? 'Aguarde enquanto o mapa é carregado.'
                  : 'Não foi possível carregar o mapa. Tente novamente mais tarde.'
              }
              shaped="false"
              iconProps={{
                name: isLoading ? 'spinner' : 'exclamation-triangle',
              }}
            />
          </Stack>
        )}

        {geoData && !isError && (
          <ComposableMapCustom
            height={550}
            width={500}
            projection="geoMercator"
            projectionConfig={projectionCofig}
            style={{ width: '100%', height: 'auto' }}
          >
            <GeographiesCustom geography={geoData}>
              {({ geographies }: { geographies: GeoJsonFeature[] }) =>
                filterGeographies(geographies).map(geo => (
                  <GeographyCustom
                    key={geo.rsmKey}
                    geography={geo}
                    onClick={() => handleGeographyClick(geo)}
                    onMouseEnter={() => onStateMouseEnter?.(geo.id)}
                    onMouseLeave={() => onStateMouseLeave?.()}
                    style={getGeographyStyle(geo)}
                    tabIndex={isClickable ? 0 : -1}
                    aria-label={geo.properties?.Estado}
                  />
                ))
              }
            </GeographiesCustom>
          </ComposableMapCustom>
        )}
      </styles.MapWrapper>
    </styles.MapContainer>
  )
}

export default Map
